import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Iframe from 'react-iframe-click';

import VenueAnnouncement from '../VenueAnnouncement';
import TranslationProvider from '../../providers/TranslationProvider';
import Icon from '../Icon';
import phoneSvg from '../../svg/icons/phone.svg';
import pinSvg from '../../svg/icons/map-marker.svg';
import infoSvg from '../../svg/icons/info.svg';
import menuSvg from '../../svg/icons/menu.svg';
import Link from '../Link';
import IconSocialMedia from '../IconSocialMedia';
import getUSOpenVenueID from '../../utils/getUSOpenVenueID';
import TargetedDelivery from '../Experiments/TargetedDelivery';

import './VenueLocationSummary.scss';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';

// hack to hide the map and "Get Directions" link just for the Carbone Beach venue
const CARBONE_BEACH_ID = 60408;
const CLICK_LINK_EVENT = 'click link';

const VenueLocationSummary = (props) => {
  const {
    announcement,
    location: {
      address1,
      address2,
      city,
      state,
      postalCode,
    } = {},
    isMobile,
    mapClasses,
    menuUrl,
    name,
    phone,
    url,
    social,
    venue,
  } = props;

  const Analytics = useAnalytics();
  const handleLinkClick = (linkType) => {
    Analytics?.event(CLICK_LINK_EVENT, { 'link type': linkType });
  };

  const isCarboneBeach = venue?.id === CARBONE_BEACH_ID;
  const isUSOpen = venue?.id === getUSOpenVenueID();

  const locationQuery = encodeURIComponent(isUSOpen ? `${address1},${city},${state},${postalCode}` : `${name},${address1},${city},${state},${postalCode}`);
  const href = `https://www.google.com/maps/search/${locationQuery}`;
  const mapsSrc = `https://www.google.com/maps/embed/v1/place?key=AIzaSyC6uWRmJgoYd5wx-198bfnO41WblQ5HHig&q=${locationQuery}`;

  return (
    <div className="VenueLocationSummary" data-testid="Venue-Location-Summary">
      <div className={cx('VenueLocationSummary__content', {
        'VenueLocationSummary__content--mobile': isMobile,
      })}
      >
        {!isCarboneBeach && (
          <Iframe
            className={mapClasses}
            title="VenueLocationSummaryMap"
            width="100%"
            height="150"
            frameBorder="0"
            style={{ border: 0 }}
            src={mapsSrc}
            allowFullScreen
            onInferredClick={() => handleLinkClick('map')}
          />
        )}
        <div className="VenueLocationSummary__content__info">
          <h3>{name}</h3>
          <p>{address1}</p>
          {address2 && <p>{address2}</p>}
          <p>{`${city}, ${state} ${postalCode}`}</p>
          <IconSocialMedia
            providers={social}
            venue={name}
            city={city}
            handleLinkClick={handleLinkClick}
          />
        </div>
      </div>

      {!isCarboneBeach && (
        <Link
          className="VenueLocationSummary__row"
          href={href}
          onClick={() => handleLinkClick('directions')}
          target="_blank"
        >
          <Icon component={pinSvg} height="20" width="20" iconClass="pin" />
          <TranslationProvider translationKey="directions_link">
            <span>Get Directions</span>
          </TranslationProvider>
        </Link>
      )}
      {phone?.phoneLabel && phone?.phoneUrl && (
        <Link
          className="VenueLocationSummary__row"
          href={`tel:${phone.phoneUrl}`}
          onClick={() => handleLinkClick('telephone')}
          target="_blank"
        >
          <Icon component={phoneSvg} height="16" iconClass="phone" />
          <span>{phone.phoneLabel}</span>
        </Link>
      )}
      {typeof url === 'string' && url.length > 0 && (
        <Link
          className="VenueLocationSummary__row"
          href={url}
          onClick={() => handleLinkClick('website')}
          target="_blank"
        >
          <Icon component={infoSvg} height="16" iconClass="info" />
          <span className="VenueLocationSummary__row--text">{url}</span>
        </Link>
      )}
      {typeof menuUrl === 'string' && menuUrl && (
        <TargetedDelivery flagKey="menu_url_interns">
          <Link
            className="VenueLocationSummary__row"
            href={menuUrl}
            onClick={() => {
              handleLinkClick('menu');
              Analytics?.event('Menu Link Clicked', {
                venue_id: venue?.id,
              });
            }}
            target="_blank"
          >
            <Icon component={menuSvg} height="20px" iconClass="menu" width="20px" />
            <TranslationProvider translationKey="view_menu">
              <span>View Menu</span>
            </TranslationProvider>
          </Link>
        </TargetedDelivery>
      )}

      <VenueAnnouncement
        announcement={announcement}
        venueName={name}
        city={city}
      />
    </div>
  );
};

VenueLocationSummary.propTypes = {
  announcement: PropTypes.shape({
    external_links: PropTypes.arrayOf(PropTypes.shape({
      link: PropTypes.string,
      link_category: PropTypes.string,
      link_label: PropTypes.string,
    })),
    headline: PropTypes.string,
    subheadline: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  mapClasses: PropTypes.string,
  menuUrl: PropTypes.string,
  name: PropTypes.string.isRequired,
  phone: PropTypes.shape({
    phoneLabel: PropTypes.string,
    phoneUrl: PropTypes.string,
  }),
  social: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    value: PropTypes.string,
  })),
  url: PropTypes.string,
  venue: PropTypes.shape({
    id: PropTypes.number,
  }),
};

VenueLocationSummary.defaultProps = {
  announcement: null,
  isMobile: false,
  location: {},
  mapClasses: undefined,
  menuUrl: undefined,
  phone: {},
  social: [],
  url: undefined,
  venue: {},
};

VenueLocationSummary.displayName = 'VenueLocationSummary';
export default VenueLocationSummary;
